import {useLayoutEffect, useRef, useState} from 'react';

/**
 * Share of the spare height that goes below the hero and composer: about a
 * third goes above, which reads as upper middle in a tall, narrow drawer.
 */
const BELOW_SHARE = 0.64;
/** ChatThread's own vertical padding (8 + 24) plus its content gap and end marker (8 + 1). */
const THREAD_CHROME_PX = 41;

/**
 * The height of the space under the Ask drawer's composer before the first
 * question, which lifts the hero and composer into the upper middle of the
 * drawer; 0 once `enabled` turns off, so the space collapses and the composer
 * drops to the bottom. Measured, not estimated: the hero's height depends on
 * how its copy and suggestions wrap at the drawer's width.
 */
export const useAskLandingSpace = ({enabled}: {enabled: boolean}) => {
  const paneRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  // Layout effect: the first measurement lands before paint, so the hero never
  // flashes at the bottom first.
  useLayoutEffect(() => {
    const pane = paneRef.current;
    const hero = heroRef.current;
    const composer = composerRef.current;
    if (!enabled || !pane || !hero || !composer) {
      return;
    }
    const measure = () => {
      const spare =
        pane.clientHeight - hero.offsetHeight - composer.offsetHeight - THREAD_CHROME_PX;
      setHeight(Math.max(0, Math.round(spare * BELOW_SHARE)));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(measure);
    [pane, hero, composer].forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [enabled]);

  return {paneRef, heroRef, composerRef, height: enabled ? height : 0};
};
